/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/DynamicDestinationSections/GeneralSection.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    General section component for Dynamic Destination form.
────────────────────────────────────────────────────────────────*/

import { Share2 } from 'lucide-react';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import Toggle from '../../../../components/ui/Toggle';
import SimpleTooltip from '../../../../components/ui/SimpleTooltip';
import { FormBox } from '../../../../components/ui/FormBox';
import ModuleDestinationSelect from '../../../../components/ui/ModuleDestinationSelect';
import { FormState, ValidationErrors, SourceType } from '../../types';
import { useI18n } from '../../../../hooks/useI18n';

interface GeneralSectionProps {
  formData: FormState;
  errors: ValidationErrors;
  onFieldChange: <K extends keyof FormState>(field: K, value: FormState[K]) => void;
  onDefaultModuleChange: (value: string) => void;
}

export function GeneralSection({
  formData,
  errors,
  onFieldChange,
  onDefaultModuleChange
}: GeneralSectionProps) {
  const { t } = useI18n();

  const isDatabaseSource = formData.sourceType === 'DB';
  const isHttpSource = formData.sourceType === 'URL';

  return (
    <FormBox
      title={t('dynamicDestination.sections.general')}
      description={t('dynamicDestination.sections.generalDescription')}
      icon={<Share2 className="h-5 w-5" />}
    >
      <div className="grid grid-cols-8 gap-x-6 gap-y-4 items-center">
        <SimpleTooltip
          content={t('dynamicDestination.fields.name.tooltip')}
          className="col-span-2"
        >
          <Label htmlFor="name" className="text-left">
            {t('dynamicDestination.fields.name.label')}
            <span className="ml-1 text-destructive">*</span>
          </Label>
        </SimpleTooltip>
        <div className="col-span-2">
          <Input
            id="name"
            value={formData.name}
            onChange={(event) => onFieldChange('name', event.target.value)}
            placeholder={t('dynamicDestination.fields.name.placeholder')}
            error={errors.name}
          />
        </div>

        <SimpleTooltip
          content={t('dynamicDestination.fields.description.tooltip')}
          className="col-span-2"
        >
          <Label htmlFor="description" className="text-left">
            {t('dynamicDestination.fields.description.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-2">
          <Input
            id="description"
            value={formData.description}
            onChange={(event) => onFieldChange('description', event.target.value)}
            placeholder={t('dynamicDestination.fields.description.placeholder')}
          />
        </div>

        <SimpleTooltip
          content={t('dynamicDestination.fields.sourceType.tooltip')}
          className="col-span-2"
        >
          <Label htmlFor="sourceType" className="text-left">
            {t('dynamicDestination.fields.sourceType.label')}
            <span className="ml-1 text-destructive">*</span>
          </Label>
        </SimpleTooltip>
        <div className="col-span-2">
          <select
            id="sourceType"
            value={formData.sourceType}
            onChange={(event) =>
              onFieldChange('sourceType', event.target.value as SourceType)
            }
            className={`w-full rounded-md border border-border bg-background px-3 py-2 text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.sourceType ? 'border-destructive focus:ring-destructive' : ''
            }`}
          >
            <option value="DB">{t('dynamicDestination.sourceTypes.DB')}</option>
            <option value="URL">{t('dynamicDestination.sourceTypes.URL')}</option>
          </select>
          {errors.sourceType ? (
            <p className="mt-1 text-xs text-destructive">{errors.sourceType}</p>
          ) : null}
        </div>

        <SimpleTooltip
          content={t('dynamicDestination.fields.matchField.tooltip')}
          className="col-span-2"
        >
          <Label htmlFor="matchField" className="text-left">
            {t('dynamicDestination.fields.matchField.label')}
            <span className="ml-1 text-destructive">*</span>
          </Label>
        </SimpleTooltip>
        <div className="col-span-2">
          <Input
            id="matchField"
            value={formData.matchField}
            onChange={(event) => onFieldChange('matchField', event.target.value)}
            placeholder={t('dynamicDestination.fields.matchField.placeholder')}
            error={errors.matchField}
          />
        </div>

        {isDatabaseSource && (
          <>
            <SimpleTooltip
              content={t('dynamicDestination.fields.sourceConfigDb.tooltip')}
              className="col-span-2"
            >
              <Label htmlFor="sourceConfig" className="text-left">
                {t('dynamicDestination.fields.sourceConfigDb.label')}
                <span className="ml-1 text-destructive">*</span>
              </Label>
            </SimpleTooltip>
            <div className="col-span-6">
              <Input
                id="sourceConfig"
                value={formData.sourceConfig}
                onChange={(event) => onFieldChange('sourceConfig', event.target.value)}
                placeholder={t('dynamicDestination.fields.sourceConfigDb.placeholder')}
                error={errors.sourceConfig}
              />
            </div>

            <SimpleTooltip
              content={t('dynamicDestination.fields.queryTemplateDb.tooltip')}
              className="col-span-2"
            >
              <Label htmlFor="queryTemplate" className="text-left">
                {t('dynamicDestination.fields.queryTemplateDb.label')}
                <span className="ml-1 text-destructive">*</span>
              </Label>
            </SimpleTooltip>
            <div className="col-span-6">
              <Input
                id="queryTemplate"
                value={formData.queryTemplate}
                onChange={(event) => onFieldChange('queryTemplate', event.target.value)}
                placeholder={t('dynamicDestination.fields.queryTemplateDb.placeholder')}
                error={errors.queryTemplate}
              />
            </div>
          </>
        )}

        {isHttpSource && (
          <>
            <SimpleTooltip
              content={t('dynamicDestination.fields.sourceConfigHttp.tooltip')}
              className="col-span-2"
            >
              <Label htmlFor="sourceConfig" className="text-left">
                {t('dynamicDestination.fields.sourceConfigHttp.label')}
                <span className="ml-1 text-destructive">*</span>
              </Label>
            </SimpleTooltip>
            <div className="col-span-6">
              <Input
                id="sourceConfig"
                value={formData.sourceConfig}
                onChange={(event) => onFieldChange('sourceConfig', event.target.value)}
                placeholder={t('dynamicDestination.fields.sourceConfigHttp.placeholder')}
                error={errors.sourceConfig}
              />
            </div>

            <SimpleTooltip
              content={t('dynamicDestination.fields.queryTemplateHttp.tooltip')}
              className="col-span-2"
            >
              <Label htmlFor="queryTemplate" className="text-left">
                {t('dynamicDestination.fields.queryTemplateHttp.label')}
                <span className="ml-1 text-destructive">*</span>
              </Label>
            </SimpleTooltip>
            <div className="col-span-6">
              <Input
                id="queryTemplate"
                value={formData.queryTemplate}
                onChange={(event) => onFieldChange('queryTemplate', event.target.value)}
                placeholder={t('dynamicDestination.fields.queryTemplateHttp.placeholder')}
                error={errors.queryTemplate}
              />
            </div>
          </>
        )}

        <SimpleTooltip
          content={t('dynamicDestination.fields.defaultDestination.tooltip')}
          className="col-span-2"
        >
          <Label htmlFor="defaultDestination" className="text-left">
            {t('dynamicDestination.fields.defaultDestination.label')}
            <span className="ml-1 text-destructive">*</span>
          </Label>
        </SimpleTooltip>
        <div className="col-span-6 space-y-1">
          <ModuleDestinationSelect
            moduleValue={formData.defaultModule || ''}
            destinationValue={formData.defaultDestination || ''}
            onModuleChange={(value) => onDefaultModuleChange(value || '')}
            onDestinationChange={(value) => onFieldChange('defaultDestination', value || '')}
            modulePlaceholder={t('dynamicDestination.fields.destinationModule.placeholder')}
            destinationPlaceholder={t('dynamicDestination.fields.defaultDestination.placeholder')}
          />
          {errors.defaultModule ? (
            <p className="text-xs text-destructive">{errors.defaultModule}</p>
          ) : null}
          {errors.defaultDestination ? (
            <p className="text-xs text-destructive">{errors.defaultDestination}</p>
          ) : null}
        </div>

        <SimpleTooltip
          content={t('dynamicDestination.fields.active.tooltip')}
          className="col-span-2"
        >
          <Label htmlFor="active" className="text-left">
            {t('dynamicDestination.fields.active.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-2">
          <Toggle
            id="active"
            value={formData.active}
            onChange={(value) => onFieldChange('active', value)}
          />
        </div>
      </div>
    </FormBox>
  );
}

